/**
 * Symphony GROUP CHAT, Phase 10a — the pure half of the group's settings and identity, its invite link and join
 * requests, delete for everyone, reports and blocks (spec /tmp/p10a/spec.md). No React and no fetch: what the wire
 * says, what a screen may offer, what a save or a refusal leaves behind. iOS `GroupAdmin.swift` and Android
 * `GroupAdmin.kt` hold the same rules; `group-chat-phase10a.test.ts` pins this one.
 *
 * The wire is common-lib's `runGroupChatAdminAction` (GroupChatAdminGateway.ts): every refusal is a typed
 * `refusal` on a 200, and a transport failure (the conversation service can answer UNIMPLEMENTED for a while after a
 * deploy) is a retry — never a crash, never a spinner left turning, never state lost.
 */
import { ownerShortName, personById, type GroupDeleted, type GroupInfoEvent, type GroupParticipant, type GroupRole, type GroupSummary, type MessageAuthor, type Translate } from './group-chat';
import { refusalKey, type GroupActionOutcome } from './group-chat-actions';

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);

// ─── Failures ────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The message for a non-ok outcome of a Phase 10a action: a refusal says its own key; a transport failure says
 * the screen's own "…Try again in a moment." key, else `chat.group.error.retryLater`.
 */
export function adminErrorKey(outcome: GroupActionOutcome<unknown>, failureKey = 'chat.group.error.retryLater'): string {
  return outcome.kind === 'refused' ? refusalKey(outcome.refusal) : failureKey;
}

// ─── The group's identity, settings and the caller's capabilities ────────────────────────────────────────────

export type GroupPermission = 'ALL_MEMBERS' | 'ADMINS_ONLY';

export interface GroupSettings {
  whoCanAddPeople: GroupPermission;
  whoCanEditInfo: GroupPermission;
  whoCanSendMessages: GroupPermission;
  joinApproval: boolean;
  /** int64 as a decimal string; sent back unchanged on update (STALE_SETTINGS otherwise). */
  revision: string;
}

export interface GroupCapabilities {
  canAddPeople: boolean;
  canEditInfo: boolean;
  canSendMessages: boolean;
  canModerate: boolean;
}

/** The service's defaults: adding people and editing info are admins'; everyone may send. */
export const DEFAULT_GROUP_SETTINGS: GroupSettings = {
  whoCanAddPeople: 'ADMINS_ONLY',
  whoCanEditInfo: 'ADMINS_ONLY',
  whoCanSendMessages: 'ALL_MEMBERS',
  joinApproval: false,
  revision: '0',
};

const perm = (v: unknown, fallback: GroupPermission): GroupPermission => (v === 'ALL_MEMBERS' || v === 'ADMINS_ONLY' ? v : fallback);

export function parseGroupSettings(raw: unknown): GroupSettings {
  if (!isObject(raw)) return { ...DEFAULT_GROUP_SETTINGS };
  return {
    whoCanAddPeople: perm(raw.whoCanAddPeople, DEFAULT_GROUP_SETTINGS.whoCanAddPeople),
    whoCanEditInfo: perm(raw.whoCanEditInfo, DEFAULT_GROUP_SETTINGS.whoCanEditInfo),
    whoCanSendMessages: perm(raw.whoCanSendMessages, DEFAULT_GROUP_SETTINGS.whoCanSendMessages),
    joinApproval: raw.joinApproval === true,
    revision: typeof raw.revision === 'string' && raw.revision ? raw.revision : typeof raw.revision === 'number' ? String(raw.revision) : '0',
  };
}

/** What the caller may do — the server's `yourCapabilities`, else derived from the role (an owner/admin everything, a member only sending). */
export function parseGroupCapabilities(raw: unknown, role: GroupRole | null | undefined): GroupCapabilities {
  if (isObject(raw)) {
    return {
      canAddPeople: raw.canAddPeople === true,
      canEditInfo: raw.canEditInfo === true,
      canSendMessages: raw.canSendMessages === true,
      canModerate: raw.canModerate === true,
    };
  }
  const admin = role === 'OWNER' || role === 'ADMIN';
  return { canAddPeople: admin, canEditInfo: admin, canSendMessages: true, canModerate: admin };
}

/** The group's own identity, as `members` and every Phase 10a `group` answer carry it. */
export interface GroupDetails {
  conversationId: string;
  name: string;
  /** '' = none. */
  description: string;
  photoUrl: string | null;
  settings: GroupSettings;
}

export function parseGroupDetails(raw: unknown): GroupDetails | null {
  if (!isObject(raw)) return null;
  const conversationId = str(raw.conversationId);
  if (!conversationId) return null;
  return {
    conversationId,
    name: typeof raw.name === 'string' ? raw.name : '',
    description: typeof raw.description === 'string' ? raw.description : '',
    photoUrl: str(raw.photoUrl) ?? null,
    settings: parseGroupSettings(raw.settings),
  };
}

/** The caller's capabilities: the `members` read's when it answered, else derived from the summary's role. */
export function capabilitiesFor(read: { capabilities?: GroupCapabilities | null } | null | undefined, role: GroupRole | null | undefined): GroupCapabilities {
  return read?.capabilities ?? parseGroupCapabilities(null, role);
}

/** Which rows the group info screen offers. */
export function infoRows(caps: GroupCapabilities): { editInfo: boolean; settings: boolean; link: boolean; requests: boolean } {
  return { editInfo: caps.canEditInfo, settings: caps.canModerate, link: caps.canAddPeople, requests: caps.canModerate };
}

/** The join-requests badge: the count, or null at 0 (hidden). */
export function requestsBadge(count: number): number | null {
  return count > 0 ? count : null;
}

/** The composer: the input, or the centred "Only admins can send messages" line. */
export function composerMode(caps: GroupCapabilities | null): 'input' | 'adminsOnly' {
  return caps && !caps.canSendMessages ? 'adminsOnly' : 'input';
}

// ─── Settings: save on each change, optimistic, revert on failure, re-seed on STALE_SETTINGS ─────────────────

export type GroupSettingKey = 'whoCanAddPeople' | 'whoCanEditInfo' | 'whoCanSendMessages' | 'joinApproval';

/** The optimistic draft after one change (same revision — the one last read). */
export function settingsChange<K extends GroupSettingKey>(current: GroupSettings, key: K, value: GroupSettings[K]): GroupSettings {
  return { ...current, [key]: value };
}

/** `update-settings`: ALL four values and the revision last read. */
export function updateSettingsBody(conversationId: string, settings: GroupSettings): Record<string, unknown> {
  return {
    conversationId,
    settings: {
      whoCanAddPeople: settings.whoCanAddPeople,
      whoCanEditInfo: settings.whoCanEditInfo,
      whoCanSendMessages: settings.whoCanSendMessages,
      joinApproval: settings.joinApproval,
      revision: settings.revision,
    },
  };
}

/**
 * What a save leaves: ok → the answered group's settings (the new revision); STALE_SETTINGS → the answered group's
 * settings with the stale message; any other refusal or a failure → the settings before the change.
 */
export function settingsSaveResult(
  before: GroupSettings,
  draft: GroupSettings,
  outcome: GroupActionOutcome,
): { settings: GroupSettings; details: GroupDetails | null; messageKey: string | null } {
  if (outcome.kind === 'ok') {
    const details = parseGroupDetails(outcome.data.group);
    return { settings: details?.settings ?? draft, details, messageKey: null };
  }
  if (outcome.kind === 'refused' && outcome.refusal === 'STALE_SETTINGS') {
    const details = parseGroupDetails(outcome.data.group);
    return { settings: details?.settings ?? before, details, messageKey: 'chat.group.error.staleSettings' };
  }
  return { settings: before, details: null, messageKey: adminErrorKey(outcome, 'chat.group.settings.saveFailed') };
}

// ─── Name, description, photo ────────────────────────────────────────────────────────────────────────────────

export const GROUP_DESCRIPTION_MAX = 512;
export const GROUP_NAME_MAX = 200;

/** A description as typed, hard-limited to 512 (by code point, so a surrogate pair is never split). */
export function clampDescription(text: string): string {
  const chars = Array.from(text);
  return chars.length > GROUP_DESCRIPTION_MAX ? chars.slice(0, GROUP_DESCRIPTION_MAX).join('') : text;
}

export function descriptionCount(text: string): number {
  return Array.from(text).length;
}

/** `update-info` with only the fields that changed; null when nothing did (or a name was blanked). */
export function updateInfoBody(
  conversationId: string,
  current: Pick<GroupDetails, 'name' | 'description' | 'photoUrl'>,
  change: { name?: string; description?: string; photoUrl?: string },
): Record<string, unknown> | null {
  const body: Record<string, unknown> = { conversationId };
  if (change.name !== undefined) {
    const name = change.name.trim();
    if (!name) return null;
    if (name !== current.name) body.name = name;
  }
  if (change.description !== undefined) {
    const description = clampDescription(change.description.trim());
    if (description !== current.description) body.description = description;
  }
  if (change.photoUrl !== undefined && change.photoUrl !== (current.photoUrl ?? '')) body.photoUrl = change.photoUrl;
  return Object.keys(body).length > 1 ? body : null;
}

// ─── Invite link ─────────────────────────────────────────────────────────────────────────────────────────────

export interface GroupInviteLink {
  url: string;
  qrUrl: string;
  token: string;
  createdDate: string | null;
}

export function parseInviteLink(raw: unknown): GroupInviteLink | null {
  if (!isObject(raw)) return null;
  const url = str(raw.url);
  const token = str(raw.token);
  if (!url || !token) return null;
  return { url, qrUrl: str(raw.qrUrl) ?? url, token, createdDate: str(raw.createdDate) ?? null };
}

export type LinkState = { kind: 'loading' } | { kind: 'shown'; link: GroupInviteLink } | { kind: 'error'; messageKey: string };

/** The link dialog after `invite-link` / `reset-invite-link`. A failed reset keeps the link that was shown. */
export function linkStateFor(outcome: GroupActionOutcome, previous: LinkState = { kind: 'loading' }): LinkState {
  const link = outcome.kind === 'ok' ? parseInviteLink(outcome.data.link) : null;
  if (link) return { kind: 'shown', link };
  if (previous.kind === 'shown') return previous;
  return { kind: 'error', messageKey: outcome.kind === 'refused' ? refusalKey(outcome.refusal) : 'chat.group.link.loadFailed' };
}

// ─── Join by link ────────────────────────────────────────────────────────────────────────────────────────────

/** `<conversationId>.<secret>` as the link carries it — case preserved; anything else is not a link token. */
export function parseLinkToken(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const token = raw.trim();
  return /^[A-Za-z0-9._~-]{1,120}$/.test(token) ? token : null;
}

export interface GroupLinkPreview {
  conversationId: string | null;
  name: string;
  photoUrl: string | null;
  memberCount: number;
  joinApproval: boolean;
  alreadyMember: boolean;
  requestPending: boolean;
}

export function parseLinkPreview(raw: unknown): GroupLinkPreview | null {
  if (!isObject(raw)) return null;
  return {
    conversationId: str(raw.conversationId) ?? null,
    name: typeof raw.name === 'string' ? raw.name : '',
    photoUrl: str(raw.photoUrl) ?? null,
    memberCount: typeof raw.memberCount === 'number' && raw.memberCount > 0 ? Math.floor(raw.memberCount) : 0,
    joinApproval: raw.joinApproval === true,
    alreadyMember: raw.alreadyMember === true,
    requestPending: raw.requestPending === true,
  };
}

/** The join-by-link screen. `retry` = a failure the reader can try again (loading the preview, or joining). */
export type JoinScreen =
  | { kind: 'join'; preview: GroupLinkPreview }
  | { kind: 'request'; preview: GroupLinkPreview }
  | { kind: 'alreadyMember'; preview: GroupLinkPreview }
  | { kind: 'pending'; preview: GroupLinkPreview | null }
  | { kind: 'invalid' }
  | { kind: 'rateLimited' }
  | { kind: 'needsAccount' }
  | { kind: 'error'; messageKey: string; retry: boolean };

function previewScreen(preview: GroupLinkPreview): JoinScreen {
  if (preview.alreadyMember) return { kind: 'alreadyMember', preview };
  if (preview.requestPending) return { kind: 'pending', preview };
  return preview.joinApproval ? { kind: 'request', preview } : { kind: 'join', preview };
}

function joinRefusalScreen(refusal: string): JoinScreen {
  if (refusal === 'INVITE_LINK_INVALID') return { kind: 'invalid' };
  if (refusal === 'JOIN_RATE_LIMITED') return { kind: 'rateLimited' };
  if (refusal === 'NOT_A_SYMPHONY_ACCOUNT') return { kind: 'needsAccount' };
  return { kind: 'error', messageKey: refusalKey(refusal), retry: false };
}

/** The screen a `preview-link` answer draws. */
export function joinScreenFor(outcome: GroupActionOutcome): JoinScreen {
  if (outcome.kind === 'refused') return joinRefusalScreen(outcome.refusal);
  const preview = outcome.kind === 'ok' ? parseLinkPreview(outcome.data.preview) : null;
  if (!preview) return { kind: 'error', messageKey: 'chat.group.join.loadFailed', retry: true };
  return previewScreen(preview);
}

/** What a `join-by-link` answer does: open the chat, or draw a screen (a failure keeps the preview with a retry message). */
export type JoinResult = { kind: 'open'; conversationId: string } | { kind: 'screen'; screen: JoinScreen; messageKey?: string };
export function joinResultFor(outcome: GroupActionOutcome, before: JoinScreen): JoinResult {
  const preview = 'preview' in before ? before.preview : null;
  if (outcome.kind === 'ok') {
    if (outcome.data.joined === true) {
      const group = isObject(outcome.data.group) ? outcome.data.group : null;
      const conversationId = str(group?.conversationId) ?? preview?.conversationId ?? null;
      if (conversationId) return { kind: 'open', conversationId };
    }
    if (outcome.data.pendingApproval === true) return { kind: 'screen', screen: { kind: 'pending', preview } };
    return { kind: 'screen', screen: before, messageKey: 'chat.group.join.loadFailed' };
  }
  if (outcome.kind === 'refused') return { kind: 'screen', screen: joinRefusalScreen(outcome.refusal) };
  return { kind: 'screen', screen: before, messageKey: 'chat.group.join.loadFailed' };
}

/** The line a join screen's state says (null: the preview itself). */
export const JOIN_SCREEN_KEYS: Partial<Record<JoinScreen['kind'], string>> = {
  alreadyMember: 'chat.group.join.alreadyMember',
  pending: 'chat.group.join.pending',
  invalid: 'chat.group.join.invalid',
  rateLimited: 'chat.group.join.rateLimited',
};

// ─── Join requests ───────────────────────────────────────────────────────────────────────────────────────────

export interface GroupJoinRequest {
  requestId: string;
  metaSiteId: string | null;
  name: string;
  avatarUrl: string | null;
  requestedDate: string | null;
}

export function parseJoinRequests(raw: unknown): GroupJoinRequest[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((r): GroupJoinRequest[] => {
    if (!isObject(r)) return [];
    const requestId = str(r.requestId);
    if (!requestId) return [];
    return [{ requestId, metaSiteId: str(r.metaSiteId) ?? null, name: typeof r.name === 'string' ? r.name : '', avatarUrl: str(r.avatarUrl) ?? null, requestedDate: str(r.requestedDate) ?? null }];
  });
}

/**
 * A `resolve-join-request` answer: resolved (the row goes, with its toast) · unavailable (the row goes, with the
 * refusal's message) · error (the row stays, the reader can try again).
 */
export type ResolveResult =
  | { kind: 'resolved'; toastKey: string; vars?: Record<string, string> }
  | { kind: 'gone'; messageKey: string }
  | { kind: 'error'; messageKey: string };
export function resolveRequestResult(outcome: GroupActionOutcome, approve: boolean, name: string): ResolveResult {
  if (outcome.kind === 'ok') return approve ? { kind: 'resolved', toastKey: 'chat.group.requests.approved', vars: { name } } : { kind: 'resolved', toastKey: 'chat.group.requests.denied' };
  if (outcome.kind === 'refused' && outcome.refusal === 'JOIN_REQUEST_UNAVAILABLE') return { kind: 'gone', messageKey: 'chat.group.error.joinRequestUnavailable' };
  return { kind: 'error', messageKey: adminErrorKey(outcome) };
}

// ─── Delete for everyone, tombstones, the row menu ───────────────────────────────────────────────────────────

/** "You deleted this message" when the deleter is the viewer, else "This message was deleted". */
export function tombstoneKey(deleted: GroupDeleted, viewerId: string | undefined): string {
  return viewerId && deleted.deletedByMetaSiteId === viewerId ? 'chat.group.message.youDeleted' : 'chat.group.message.deleted';
}

interface MenuRow {
  id?: string;
  role?: string;
  author?: MessageAuthor | null;
  groupPost?: unknown;
  groupEvent?: unknown;
  groupApproval?: unknown;
  maestroUnavailable?: unknown;
  groupMeta?: { deleted?: unknown; infoEvent?: unknown; workStopped?: unknown; chainStopped?: unknown } | null;
}

/** Whether the viewer authored the row: their own post, or their own Maestro's reply. */
export function isMyRow(row: MenuRow, viewerId: string | undefined): boolean {
  if (!viewerId) return false;
  const a = row.author;
  if (!a) return row.role === 'USER';
  if (a.kind === 'HUMAN') return a.principalMetaSiteId === viewerId;
  if (a.kind === 'MAESTRO') return (a.agentOwnerMetaSiteId ?? a.principalMetaSiteId) === viewerId;
  return false;
}

const SERVER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The row's ⋯ menu in a live group: "Delete for everyone" on a row the viewer authored or when they can moderate;
 * "Report message" on any row they did not author. Nothing on a SYSTEM line, a tombstone, a pending post, or a row
 * with no server id.
 */
export function messageMenuItems(row: MenuRow, viewerId: string | undefined, caps: GroupCapabilities | null): ('delete' | 'report')[] {
  if (!row.id || !SERVER_ID.test(row.id) || row.groupPost) return [];
  if (row.role === 'SYSTEM' || row.author?.kind === 'SYSTEM') return [];
  if (row.groupEvent || row.groupMeta?.deleted || row.groupMeta?.infoEvent || row.groupMeta?.workStopped || row.groupMeta?.chainStopped) return [];
  if (row.groupApproval || row.maestroUnavailable) return [];
  if (!row.author) return [];
  const mine = isMyRow(row, viewerId);
  const items: ('delete' | 'report')[] = [];
  if (mine || caps?.canModerate) items.push('delete');
  if (!mine) items.push('report');
  return items;
}

// ─── Reports ─────────────────────────────────────────────────────────────────────────────────────────────────

export type ReportReason = 'SPAM' | 'HARASSMENT' | 'INAPPROPRIATE' | 'SCAM' | 'OTHER';
export const REPORT_REASONS: readonly ReportReason[] = ['SPAM', 'HARASSMENT', 'INAPPROPRIATE', 'SCAM', 'OTHER'];
export const REPORT_REASON_KEYS: Record<ReportReason, string> = {
  SPAM: 'chat.group.report.reason.spam',
  HARASSMENT: 'chat.group.report.reason.harassment',
  INAPPROPRIATE: 'chat.group.report.reason.inappropriate',
  SCAM: 'chat.group.report.reason.scam',
  OTHER: 'chat.group.report.reason.other',
};
export const REPORT_DETAILS_MAX = 1000;

export interface ReportForm {
  reason: ReportReason | null;
  details: string;
  leave: boolean;
}
export const EMPTY_REPORT_FORM: ReportForm = { reason: null, details: '', leave: false };

/** Report stays off until a reason is chosen; details are optional and at most 1000. */
export function reportFormValid(form: ReportForm): boolean {
  return form.reason !== null && Array.from(form.details.trim()).length <= REPORT_DETAILS_MAX;
}

/** "Also leave the group" — never for the OWNER, who cannot leave. */
export function reportLeaveOffered(role: GroupRole | null | undefined): boolean {
  return role !== 'OWNER';
}

export type ReportTarget = { kind: 'group' } | { kind: 'message'; messageId: string };

export function reportBody(conversationId: string, target: ReportTarget, form: ReportForm, role: GroupRole | null | undefined): { action: 'report-group' | 'report-message'; body: Record<string, unknown> } | null {
  if (!reportFormValid(form) || !form.reason) return null;
  const details = form.details.trim();
  const leave = form.leave && reportLeaveOffered(role);
  return {
    action: target.kind === 'group' ? 'report-group' : 'report-message',
    body: {
      conversationId,
      ...(target.kind === 'message' ? { messageId: target.messageId } : {}),
      reason: form.reason,
      ...(details ? { details } : {}),
      ...(leave ? { leave: true } : {}),
    },
  };
}

export type ReportResult = { kind: 'done'; left: boolean } | { kind: 'error'; messageKey: string };
export function reportResult(outcome: GroupActionOutcome): ReportResult {
  if (outcome.kind === 'ok') return { kind: 'done', left: outcome.data.left === true };
  return { kind: 'error', messageKey: outcome.kind === 'refused' ? refusalKey(outcome.refusal) : 'chat.group.report.failed' };
}

// ─── Blocks ──────────────────────────────────────────────────────────────────────────────────────────────────

export interface BlockedPerson {
  metaSiteId: string;
  name: string;
  avatarUrl: string | null;
  blockedDate: string | null;
}

export function parseBlockedPerson(raw: unknown): BlockedPerson | null {
  if (!isObject(raw)) return null;
  const metaSiteId = str(raw.metaSiteId);
  if (!metaSiteId) return null;
  return { metaSiteId, name: typeof raw.name === 'string' ? raw.name : '', avatarUrl: str(raw.avatarUrl) ?? null, blockedDate: str(raw.blockedDate) ?? null };
}

export function parseBlockedList(raw: unknown): BlockedPerson[] {
  return Array.isArray(raw) ? raw.map(parseBlockedPerson).filter((p): p is BlockedPerson => p !== null) : [];
}

/** Block / Unblock on a participant row: every HUMAN member other than the viewer; never a Maestro, never me. */
export function blockOffered(p: Pick<GroupParticipant, 'id' | 'kind'>, viewerId: string | undefined): boolean {
  return p.kind === 'HUMAN' && !!viewerId && p.id !== viewerId;
}

/** The blocked list after a block (newest first, deduped) or an unblock. */
export function withBlocked(list: BlockedPerson[], person: BlockedPerson): BlockedPerson[] {
  return [person, ...list.filter((p) => p.metaSiteId !== person.metaSiteId)];
}
export function withoutBlocked(list: BlockedPerson[], metaSiteId: string): BlockedPerson[] {
  return list.filter((p) => p.metaSiteId !== metaSiteId);
}

// ─── The Phase 10a SYSTEM lines ──────────────────────────────────────────────────────────────────────────────

const EVENT_KEYS: Record<string, string> = {
  NAME_CHANGED: 'chat.group.event.nameChanged',
  DESCRIPTION_CHANGED: 'chat.group.event.descriptionChanged',
  PHOTO_CHANGED: 'chat.group.event.photoChanged',
  PHOTO_REMOVED: 'chat.group.event.photoRemoved',
  JOINED_BY_LINK: 'chat.group.event.joinedByLink',
  // Phase 10b: "{name} pinned a message" (`value` is the pinned message's id).
  MESSAGE_PINNED: 'chat.group.event.messagePinned',
  // E2E: membership changes as `kind` events — an accepted invite, a leave, the owner ending the group.
  MEMBER_JOINED: 'chat.group.event.joined',
  MEMBER_LEFT: 'chat.group.event.left',
  GROUP_ENDED: 'chat.group.event.ended',
};
const SETTING_KEYS: Record<string, [restricted: string, open: string]> = {
  WHO_CAN_ADD_PEOPLE: ['chat.group.event.settingsAddAdmins', 'chat.group.event.settingsAddAll'],
  WHO_CAN_EDIT_INFO: ['chat.group.event.settingsEditAdmins', 'chat.group.event.settingsEditAll'],
  WHO_CAN_SEND_MESSAGES: ['chat.group.event.settingsSendAdmins', 'chat.group.event.settingsSendAll'],
  JOIN_APPROVAL: ['chat.group.event.settingsApprovalOn', 'chat.group.event.settingsApprovalOff'],
};

/** The kinds that are about one member (joined, left, removed): the line names that member, not only the actor. */
const MEMBER_KINDS = new Set(['MEMBER_JOINED', 'MEMBER_LEFT']);

/** The i18n key an info event renders with, or null (unknown kind or setting → the row's own text). */
export function infoEventKey(event: GroupInfoEvent): string | null {
  if (event.kind === 'SETTING_CHANGED') {
    const pair = event.setting ? SETTING_KEYS[event.setting] : undefined;
    return pair ? pair[event.restricted === true ? 0 : 1] : null;
  }
  if (event.kind === 'MEMBER_REMOVED') return 'chat.group.event.memberRemoved';
  return EVENT_KEYS[event.kind] ?? null;
}

/**
 * One person's `{name}`: "You" for the viewer, else their first name from the members, else the first name the
 * event itself carries (someone who has since left), else null.
 */
function personName(id: string | null | undefined, eventName: string | undefined, summary: GroupSummary | null, t: Translate): string | null {
  if (id && summary?.viewerId && id === summary.viewerId) return t('chat.group.you');
  const p = personById(summary, id);
  const name = p ? ownerShortName(p, t) : '';
  if (name) return name;
  const own = eventName ? ownerShortName({ displayName: eventName }, t) : '';
  return own || null;
}

/** `{name}`: "You" for the viewer, else the actor's first name from the members; null when it cannot be resolved. */
export function infoEventActorName(event: GroupInfoEvent, summary: GroupSummary | null, t: Translate): string | null {
  return personName(event.actorMetaSiteId, event.actorDisplayName, summary, t);
}

/**
 * The centred line of a group SYSTEM row with a `kind`, always from the metadata (web, iOS `GroupChatAdmin`, Android
 * `GroupAdmin` decide the same): a known kind renders its localized line, a person nobody can name reading "Someone";
 * an unknown kind (or setting) renders the row's own `content`. Null only when there is nothing at all to draw.
 */
export function groupInfoEventLine(event: GroupInfoEvent, content: string | null | undefined, summary: GroupSummary | null, t: Translate): string | null {
  const key = infoEventKey(event);
  if (!key) {
    const own = content?.trim();
    return own ? own : null;
  }
  const someone = t('chat.group.someone');
  if (event.kind === 'MEMBER_REMOVED') {
    const viewer = summary?.viewerId;
    const subject = personName(event.subjectMetaSiteId, event.subjectDisplayName, summary, t) ?? someone;
    if (viewer && event.actorMetaSiteId === viewer) return t('chat.group.event.youRemoved', { name: subject });
    const actor = infoEventActorName(event, summary, t) ?? someone;
    if (viewer && event.subjectMetaSiteId === viewer) return t('chat.group.event.removedYou', { actor });
    return t('chat.group.event.memberRemoved', { actor, name: subject });
  }
  const name = MEMBER_KINDS.has(event.kind) && event.subjectMetaSiteId
    ? personName(event.subjectMetaSiteId, event.subjectDisplayName, summary, t)
    : infoEventActorName(event, summary, t);
  return t(key, { name: name ?? someone, value: event.value ?? '' });
}

/**
 * What a live `kind` row means for the viewer's own membership: removed (a MEMBER_REMOVED about them) or the group
 * ended. Null for everything else.
 */
export function lostStateFromInfoEvent(event: GroupInfoEvent, viewerId: string | null | undefined): 'removed' | 'ended' | null {
  if (event.kind === 'GROUP_ENDED') return 'ended';
  if (event.kind === 'MEMBER_REMOVED' && viewerId && event.subjectMetaSiteId === viewerId) return 'removed';
  return null;
}

/**
 * The invite link as the sheet SHOWS it (2026-10-31 rerun): scheme, query and fragment removed
 * (`symphony.wix.app/group-join/5404…`), then middle-truncated to `max` characters so it fits ONE line. Copy, Share and
 * the QR code always use the FULL link. iOS / Android `displayInviteLink` draw the same.
 */
export function displayInviteLink(url: string, max = 44): string {
  const bare = url.trim().replace(/^[a-z][a-z0-9+.-]*:\/\//i, '').replace(/[?#].*$/, '').replace(/\/+$/, '');
  const chars = Array.from(bare);
  if (chars.length <= max || max < 5) return bare;
  const tail = Math.floor((max - 1) / 3);
  const head = max - 1 - tail;
  return `${chars.slice(0, head).join('')}…${chars.slice(chars.length - tail).join('')}`;
}
